import { useRef } from 'react';
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from '@/components/ai-elements/conversation';
import { deriveAssistantVisualState, isAwaitingAskUserContinuation } from '../assistant-visual-state';
import type { ChatTurnErrorState } from '../chat-turn-error';
import type { ChatUIMessage } from '../types';
import { ChatTurnError } from './ChatTurnError';
import { MessageBubble } from './MessageBubble';
import { StreamingIndicator } from './StreamingIndicator';

type MessageListProps = {
  isAskUserSubmitting: boolean;
  isSending: boolean;
  messages: ChatUIMessage[];
  onEditMessage: (message: ChatUIMessage) => void;
  onRetryAssistant: () => void;
  onRetryTurn: () => void;
  status: 'error' | 'ready' | 'streaming' | 'submitted';
  turnError: ChatTurnErrorState | null;
};

function mergeConsecutiveMessages(messages: ChatUIMessage[]): ChatUIMessage[] {
  const merged: ChatUIMessage[] = [];
  for (const message of messages) {
    const last = merged[merged.length - 1];
    if (last && last.role === 'assistant' && message.role === 'assistant') {
      merged[merged.length - 1] = {
        ...last,
        parts: [...last.parts, ...message.parts],
      };
    } else {
      merged.push(message);
    }
  }
  return merged;
}

export function MessageList({
  isAskUserSubmitting,
  isSending,
  messages: rawMessages,
  onEditMessage,
  onRetryAssistant,
  onRetryTurn,
  status,
  turnError,
}: MessageListProps) {
  const messages = mergeConsecutiveMessages(rawMessages);
  const lastMessage = messages.at(-1);
  const isTurnActive = status === 'submitted' || status === 'streaming' || isAskUserSubmitting;
  // Ancre synchrone dès le 1er render du tour (pas en effect — sinon le
  // Reasoning démarre son chrono un frame trop tard / sans ancre).
  const turnStartedAtRef = useRef<number | null>(null);
  if (isTurnActive) {
    turnStartedAtRef.current ??= Date.now();
  } else {
    turnStartedAtRef.current = null;
  }
  const reasoningStartedAt = turnStartedAtRef.current ?? undefined;

  const lastAssistantState = lastMessage?.role === 'assistant'
    ? deriveAssistantVisualState({
      hasTurnError: turnError !== null,
      message: lastMessage,
      status,
    })
    : null;
  const isAwaitingAssistantContinuation = isTurnActive
    && lastMessage !== undefined
    && isAwaitingAskUserContinuation(lastMessage, status);
  const shouldRenderLiveIndicator = turnError === null && (
    (lastMessage?.role !== 'assistant' && isTurnActive)
    || lastAssistantState === 'thinking'
    || isAwaitingAssistantContinuation
  );
  const failedUserMessage = turnError?.failedUserMessageId
    ? messages.find((message) => message.id === turnError.failedUserMessageId)
    : undefined;

  return (
    <div className="relative min-h-0 flex-1 overflow-hidden">
      <Conversation
        aria-busy={isTurnActive}
        className="h-full"
      >
        <ConversationContent className="mx-auto flex w-full max-w-[48rem] flex-col gap-5 px-5 pb-12 pt-8 md:gap-7 md:px-6 md:pb-16 md:pt-10">
          {messages.map((message, index) => {
            const isLastAssistant = message.role === 'assistant' && index === messages.length - 1;

            return (
              <MessageBubble
                key={message.id}
                canRetry={isLastAssistant && !isSending}
                isLoading={isLastAssistant && (status === 'streaming' || status === 'submitted')}
                message={message}
                onEditMessage={onEditMessage}
                onRetryAssistant={onRetryAssistant}
                reasoningStartedAt={isLastAssistant ? reasoningStartedAt : undefined}
              />
            );
          })}
          {shouldRenderLiveIndicator ? (
            <StreamingIndicator key="active-turn-feedback" startedAt={reasoningStartedAt} />
          ) : null}
          {turnError ? (
            <ChatTurnError
              error={turnError}
              onEdit={failedUserMessage ? () => onEditMessage(failedUserMessage) : undefined}
              onRetry={onRetryTurn}
            />
          ) : null}
        </ConversationContent>
        <ConversationScrollButton className="bottom-8 border-border/50 bg-card/90 shadow-[var(--shadow-float)] backdrop-blur-lg transition-all duration-200" />
      </Conversation>
    </div>
  );
}
